import { openContactCall } from "../utils/contactCommunication";
import CompanyTableCell from "../components/CompanyTableCell";
import { interviewsChanged, openInterviewScheduler } from "../utils/interviewScheduling";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { hasGmailScope } from "../utils/gmailScopes";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TableCell,
  TextField,
  Typography,
} from "@mui/material";
import { Search as SearchIcon } from "../components/icons/FigmaControlIcons";
import {
  Filter,
  Sms,
} from "iconsax-react";

import useAuthController from "../hooks/useAuthController";
import Breadcrumb from "../components/Breadcrumb";
import BulkActionsBar from "../components/BulkActionsBar";
import CompanyProfileHeader from "../components/CompanyProfileHeader";
import GenericDataTable from "../components/GenericDataTable";
import InfoActionIcons from "../components/InfoActionIcons";
import ListTableTabsTemplate from "../components/layouts/ListTableTabsTemplate";
import StatsSlider from "../components/StatsSlider";
import TableLocationValue from "../components/TableLocationValue";
import TabNavigationBar from "../components/TabNavigationBar";
import TopListHeaderBar from "../components/TopListHeaderBar";
import CsvImportDialog from "../components/CsvImportDialog";
import useCsvImport from "../hooks/useCsvImport";
import MessagePreviewDialog from "../components/messages/MessagePreviewDialog";
import NewEmailCandidateDialog from "../components/messages/NewEmailCandidateDialog";
import DirectMessagesWorkspace from "../components/messages/DirectMessagesWorkspace";
import {
  DirectMessagesAIAgentCard,
  DirectMessagesEmailsCard,
  DirectMessagesQuickActionsCard,
} from "../components/messages/DirectMessagesSidebarWidgets";
import {
  MessagesAIAgentCard,
  MessagesQuickActionsCard,
  MessagesWriteEmailCard,
} from "../components/messages/MessagesSidebarWidgets";
import { getSidebarResetToken } from "../utils/sidebarNavigation";
import { findTabLabel } from "../utils/breadcrumbs";
import { formatLocalDate, parseDateTimeValue, toTimeValue } from "../utils/dateTime";

const PURPLE = "#9C4FFF";
const BLUE = "#3451F4";
const BORDER = "#E5E7EB";

const messageActionIconSx = { p: 0.5, color: PURPLE };
const EmailEditIcon = () => <svg className="message-tab-outline-action-icon" width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true"><path d="M11 18.7923H6.41665C3.66665 18.7923 1.83331 17.4173 1.83331 14.209V7.79232C1.83331 4.58398 3.66665 3.20898 6.41665 3.20898H15.5833C18.3333 3.20898 20.1666 4.58398 20.1666 7.79232V10.5423" stroke="#9C4FFF" strokeWidth="1.375" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" /><path d="M15.5834 8.25L12.7142 10.5417C11.77 11.2933 10.2208 11.2933 9.27668 10.5417L6.41669 8.25" stroke="#9C4FFF" strokeWidth="1.375" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" /><path d="M17.6092 13.5389L14.3642 16.7839C14.2359 16.9123 14.1167 17.1506 14.0892 17.3248L13.915 18.5623C13.8509 19.0114 14.1626 19.3231 14.6117 19.259L15.8492 19.0848C16.0234 19.0573 16.2709 18.9381 16.3901 18.8098L19.6351 15.5648C20.1942 15.0056 20.4601 14.3548 19.6351 13.5298C18.8192 12.714 18.1684 12.9798 17.6092 13.5389Z" stroke="#9C4FFF" strokeWidth="1.375" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" /><path d="M17.1417 14.0059C17.4167 14.9959 18.1867 15.7659 19.1767 16.0409" stroke="#9C4FFF" strokeWidth="1.375" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" /></svg>;
const PhoneIcon = () => <svg className="message-tab-outline-action-icon" width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true"><path d="M20.1392 16.8012C20.1392 17.1312 20.0659 17.4704 19.91 17.8004C19.7542 18.1304 19.5525 18.442 19.2867 18.7354C18.8375 19.2304 18.3425 19.5879 17.7834 19.817C17.2334 20.0462 16.6375 20.1654 15.9959 20.1654C15.0609 20.1654 14.0617 19.9454 13.0075 19.4962C11.9534 19.047 10.8992 18.442 9.85421 17.6812C8.80004 16.9112 7.80087 16.0587 6.84754 15.1145C5.90337 14.1612 5.05087 13.162 4.29004 12.117C3.53837 11.072 2.93337 10.027 2.49337 8.9912C2.05337 7.9462 1.83337 6.94703 1.83337 5.9937C1.83337 5.37036 1.94337 4.77453 2.16337 4.22453C2.38337 3.66536 2.73171 3.15203 3.21754 2.6937C3.80421 2.1162 4.44587 1.83203 5.12421 1.83203C5.38087 1.83203 5.63754 1.88703 5.86671 1.99703C6.10504 2.10703 6.31587 2.27203 6.48087 2.51036L8.60754 5.50786C8.77254 5.73703 8.89171 5.94786 8.97421 6.14953C9.05671 6.34203 9.10254 6.53453 9.10254 6.7087C9.10254 6.9287 9.03837 7.1487 8.91004 7.35953C8.79087 7.57036 8.61671 7.79036 8.39671 8.01036L7.70004 8.73453C7.59921 8.83536 7.55337 8.95453 7.55337 9.1012C7.55337 9.17453 7.56254 9.2387 7.58087 9.31203C7.60837 9.38536 7.63587 9.44036 7.65421 9.49536C7.81921 9.79786 8.10337 10.192 8.50671 10.6687C8.91921 11.1454 9.35921 11.6312 9.83587 12.117C10.3309 12.6029 10.8075 13.052 11.2934 13.4645C11.77 13.8679 12.1642 14.1429 12.4759 14.3079C12.5217 14.3262 12.5767 14.3537 12.6409 14.3812C12.7142 14.4087 12.7875 14.4179 12.87 14.4179C13.0259 14.4179 13.145 14.3629 13.2459 14.262L13.9425 13.5745C14.1717 13.3454 14.3917 13.1712 14.6025 13.0612C14.8134 12.9329 15.0242 12.8687 15.2534 12.8687C15.4275 12.8687 15.6109 12.9054 15.8125 12.9879C16.0142 13.0704 16.225 13.1895 16.4542 13.3454L19.4884 15.4995C19.7267 15.6645 19.8917 15.857 19.9925 16.0862C20.0842 16.3154 20.1392 16.5445 20.1392 16.8012Z" stroke="#9C4FFF" strokeWidth="1.375" strokeMiterlimit="10" /></svg>;
const TrashIcon = () => <svg className="message-tab-outline-action-icon" width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true"><path d="M19.25 5.48177C16.1975 5.17927 13.1267 5.02344 10.065 5.02344C8.25 5.02344 6.435 5.1151 4.62 5.29844L2.75 5.48177" fill="#9C4FFF" /><path d="M19.25 5.48177C16.1975 5.17927 13.1267 5.02344 10.065 5.02344C8.25 5.02344 6.435 5.1151 4.62 5.29844L2.75 5.48177" stroke="#9C4FFF" strokeWidth="1.375" strokeLinecap="round" strokeLinejoin="round" /><path d="M7.79169 4.55648L7.99335 3.35565C8.14002 2.48482 8.25002 1.83398 9.79919 1.83398H12.2009C13.75 1.83398 13.8692 2.52148 14.0067 3.36482L14.2084 4.55648" stroke="#9C4FFF" strokeWidth="1.375" strokeLinecap="round" strokeLinejoin="round" /><path d="M17.2791 8.37891L16.6833 17.6097C16.5824 19.0489 16.4999 20.1672 13.9424 20.1672H8.05743C5.49993 20.1672 5.41743 19.0489 5.3166 17.6097L4.72076 8.37891" stroke="#9C4FFF" strokeWidth="1.375" strokeLinecap="round" strokeLinejoin="round" /><path d="M9.46924 15.125H12.5217" stroke="#9C4FFF" strokeWidth="1.375" strokeLinecap="round" strokeLinejoin="round" /><path d="M8.70831 11.459H13.2916" stroke="#9C4FFF" strokeWidth="1.375" strokeLinecap="round" strokeLinejoin="round" /></svg>;
const MoreActionsIcon = () => <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true"><circle cx="11" cy="5" r="1.375" fill="#9C4FFF" /><circle cx="11" cy="11" r="1.375" fill="#9C4FFF" /><circle cx="11" cy="17" r="1.375" fill="#9C4FFF" /></svg>;
const LIGHT_BLUE = "#D7E6FF";
const MUTED = "#98A2B3";
const GMAIL_SEND_SCOPE = "https://www.googleapis.com/auth/gmail.send";
const GMAIL_COMPOSE_SCOPE = "https://www.googleapis.com/auth/gmail.compose";
const API_BASE_RAW = String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const API_ROOT = API_BASE_RAW.endsWith("/api")
  ? API_BASE_RAW
  : `${API_BASE_RAW}/api`;

const CHANNELS = [
  { value: "emails", label: "Emails", apiValue: "email", crumb: "Email" },
];

const WORKSPACE_FILTER_OPTIONS = [
  { value: "all", label: "All Messages" },
  { value: "unread", label: "Unread" },
  { value: "read", label: "Read" },
  { value: "sent", label: "Sent" },
];

const statusChipStyles = {
  New: { backgroundColor: "#EEF2FF", color: "#4F46E5" },
  Contacted: { backgroundColor: "#F5F3FF", color: "#7C3AED" },
  Interviewing: { backgroundColor: "#FFF7ED", color: "#EA580C" },
  Available: { backgroundColor: "#ECFDF3", color: "#16A34A" },
  Hired: { backgroundColor: "#EEF2FF", color: "#1D4ED8" },
  "Not Available": { backgroundColor: "#FEF2F2", color: "#B91C1C" },
  default: { backgroundColor: "#F3F4F6", color: "#4B5563" },
};

const getStatusChipSx = (status) => {
  const style = statusChipStyles[status] || statusChipStyles.default;
  return {
    ...style,
    borderRadius: "999px",
    fontSize: 11,
    fontWeight: 600,
    height: 22,
    px: 1.5,
    lineHeight: 1.3,
  };
};

const listColumns = [
  { id: "name", label: "Name", numeric: false },
  { id: "status", label: "Status", numeric: false },
  { id: "company", label: "Company Name", numeric: false },
  { id: "location", label: "Location", numeric: false },
  { id: "stage", label: "Stage", numeric: false },
  { id: "info", label: "Info", numeric: false },
  { id: "last_sync", label: "Last Sync", numeric: false },
  { id: "recruiter", label: "Recruiter", numeric: false },
];

const sectionColumns = [
  { id: "name", label: "Name", numeric: false },
  { id: "subject", label: "Subject", numeric: false },
  { id: "date", label: "Date", numeric: false },
  { id: "actions", label: "Actions", numeric: false },
];

const LIST_EXPORT_FIELDS = [
  { id: "candidate_name", label: "Name" },
  { id: "status", label: "Status" },
  { id: "company_name", label: "Company Name" },
  { id: "location", label: "Location" },
  { id: "stage", label: "Stage" },
  { id: "last_sync_at", label: "Last Sync" },
  { id: "recruiter_name", label: "Recruiter" },
];

const TABS_EXPORT_FIELDS = [
  { id: "candidate_name", label: "Name" },
  { id: "candidate_title", label: "Title" },
  { id: "subject", label: "Subject" },
  { id: "sent_at", label: "Date" },
  { id: "folder", label: "Folder" },
  { id: "message_provider", label: "Provider" },
];

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    ""
  );
}

function authHeaders(includeJson = false) {
  const token = getToken();
  return {
    ...(includeJson ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(token ? { "x-access-token": token } : {}),
  };
}

function apiUrl(path) {
  return `${API_ROOT}${path.startsWith("/") ? path : `/${path}`}`;
}

function getGmailOAuthParams() {
  if (typeof window === "undefined") return null;
  const url = new URL(window.location.href);
  const status = normalizeString(url.searchParams.get("gmail_oauth")).toLowerCase();
  if (!status) return null;
  return {
    status,
    email: normalizeString(url.searchParams.get("gmail_email")),
    error: normalizeString(url.searchParams.get("gmail_error")),
  };
}

function clearGmailOAuthParams() {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.delete("gmail_oauth");
  url.searchParams.delete("gmail_email");
  url.searchParams.delete("gmail_error");
  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, document.title, nextUrl);
}

async function readResponseError(response, fallbackMessage) {
  const text = await response.text().catch(() => "");
  const normalizedText = normalizeString(text);
  if (!normalizedText) return fallbackMessage;

  try {
    const payload = JSON.parse(normalizedText);
    const payloadMessage =
      payload && typeof payload === "object"
        ? normalizeString(payload.error || payload.message)
        : "";
    return payloadMessage || normalizedText;
  } catch {
    return normalizedText;
  }
}

function renderGmailPopupStatus(popup, title, message) {
  if (!popup || popup.closed) return;

  try {
    popup.document.title = title;
    popup.document.body.innerHTML = `
      <div style="font-family: Arial, sans-serif; padding: 24px; line-height: 1.5;">
        <h2 style="margin: 0 0 12px; font-size: 18px;">${title}</h2>
        <p style="margin: 0; white-space: pre-line;">${String(message || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>
      </div>
    `;
  } catch {
    // Ignore popup rendering issues and fall back to the inline error banner.
  }
}

function normalizeString(value) {
  return String(value ?? "").trim();
}

function normalizeChannelValue(value) {
  const raw = normalizeString(value).toLowerCase();
  if (raw === "email") return "emails";
  if (raw === "linkedin") return "linkedin";
  if (raw === "direct") return "direct";
  return "emails";
}

function formatRelativeTime(value) {
  if (!value) return "-";
  const timestamp = toTimeValue(value);
  if (!timestamp) return String(value);

  const diffMs = Date.now() - timestamp;
  if (diffMs < 45_000) return "now";

  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;

  return formatLocalDate(timestamp, { month: "short", day: "numeric" }, String(value));
}

function formatSectionDate(value) {
  if (!value) return "-";
  return formatLocalDate(value, { day: "numeric", month: "short" }, String(value));
}

function formatWorkspaceMetaDate(value) {
  if (!value) return "";
  const parsed = parseDateTimeValue(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  const parts = new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).formatToParts(parsed);
  const getPart = (type) => parts.find((part) => part.type === type)?.value || "";
  return [getPart("weekday"), getPart("day"), getPart("month"), getPart("year")]
    .filter(Boolean)
    .join(" ");
}

function formatStatValue(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString() : String(value || "0");
}

function ensureAttachments(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((item) => item && typeof item === "object");
}

function serializeComposerAttachments(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      const file = item?.file;
      const name = normalizeString(item?.name || file?.name);
      if (!name) return null;

      const size = Number(item?.size || file?.size || 0);
      const meta = normalizeString(item?.type || item?.meta || file?.type);

      return {
        name,
        ...(Number.isFinite(size) && size > 0 ? { size } : {}),
        ...(meta ? { meta } : {}),
      };
    })
    .filter(Boolean);
}

function buildGmailComposeFormData(payload = {}, attachments = []) {
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value == null) return;
    const normalizedValue =
      Array.isArray(value) || (value && typeof value === "object")
        ? JSON.stringify(value)
        : String(value);
    formData.append(key, normalizedValue);
  });

  attachments.forEach((attachment) => {
    if (typeof File === "undefined" || !(attachment?.file instanceof File)) return;
    formData.append("attachments", attachment.file, attachment.name || attachment.file.name);
  });

  return formData;
}

function ensureStringList(value) {
  if (!Array.isArray(value)) return [];
  return value.map((item) => normalizeString(item)).filter(Boolean);
}

function ensureObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function normalizeMessage(message) {
  return {
    id: Number(message?.id || 0),
    channel: normalizeChannelValue(message?.channel),
    folder: normalizeString(message?.folder || "unread").toLowerCase(),
    candidate_name: normalizeString(message?.candidate_name),
    candidate_title: normalizeString(message?.candidate_title),
    candidate_email: normalizeString(message?.candidate_email || message?.to_email),
    candidate_phone: normalizeString(message?.candidate_phone),
    company_name: normalizeString(message?.company_name),
    location: normalizeString(message?.location),
    stage: normalizeString(message?.stage),
    status: normalizeString(message?.status),
    recruiter_name: normalizeString(message?.recruiter_name),
    recruiter_email: normalizeString(message?.recruiter_email),
    subject: normalizeString(message?.subject),
    preview_text: normalizeString(message?.preview_text),
    body_text: normalizeString(message?.body_text),
    from_name: normalizeString(message?.from_name),
    from_email: normalizeString(message?.from_email),
    to_email: normalizeString(message?.to_email || message?.candidate_email),
    cc_email: normalizeString(message?.cc_email),
    bcc_email: normalizeString(message?.bcc_email),
    sent_at: message?.sent_at || null,
    last_sync_at: message?.last_sync_at || message?.sent_at || null,
    avatar_url: normalizeString(message?.avatar_url),
    linkedin_url: normalizeString(message?.linkedin_url),
    email_url: normalizeString(message?.email_url),
    sms_url: normalizeString(message?.sms_url),
    priority: normalizeString(message?.priority || "High"),
    job_title: normalizeString(message?.job_title),
    contact_name: normalizeString(message?.contact_name),
    contact_email: normalizeString(message?.contact_email),
    hiring_manager: normalizeString(message?.hiring_manager),
    last_status: normalizeString(message?.last_status),
    top_match_label: normalizeString(message?.top_match_label),
    attachments: ensureAttachments(message?.attachments),
    message_source: normalizeString(
      message?.message_source || message?.source || "manual"
    ).toLowerCase(),
    message_provider: normalizeString(message?.message_provider).toLowerCase(),
    sequence_id: Number(message?.sequence_id || 0),
    sequence_step_id: Number(message?.sequence_step_id || 0),
    sequence_enrollment_id: Number(message?.sequence_enrollment_id || 0),
    sequence_name: normalizeString(message?.sequence_name),
    gmail_message_id: normalizeString(message?.gmail_message_id),
    gmail_thread_id: normalizeString(message?.gmail_thread_id),
    gmail_label_ids: ensureStringList(message?.gmail_label_ids),
    gmail_synced_at: message?.gmail_synced_at || null,
    message_metadata: ensureObject(message?.message_metadata),
  };
}

function normalizeChat(chat) {
  return {
    id: Number(chat?.id || 0),
    name: normalizeString(chat?.name),
    handle: normalizeString(chat?.handle),
    role: normalizeString(chat?.role),
    preview: normalizeString(chat?.preview),
    preview_type: normalizeString(chat?.preview_type || "text"),
    preview_attachment_name: normalizeString(chat?.preview_attachment_name),
    preview_attachment_size: normalizeString(chat?.preview_attachment_size),
    timestamp: normalizeString(chat?.timestamp),
    status: normalizeString(chat?.status || "offline"),
    avatar_url: normalizeString(chat?.avatar_url),
    candidate_email: normalizeString(chat?.candidate_email),
    candidate_phone: normalizeString(chat?.candidate_phone),
    company_name: normalizeString(chat?.company_name),
    recruiter_name: normalizeString(chat?.recruiter_name),
    recruiter_date_label: normalizeString(chat?.recruiter_date_label),
    unread_count: Number(chat?.unread_count || 0),
    is_online:
      Boolean(chat?.is_online) ||
      normalizeString(chat?.status || "offline").toLowerCase() === "online",
    last_message_at: chat?.last_message_at || null,
    email_url: normalizeString(chat?.email_url),
    linkedin_url: normalizeString(chat?.linkedin_url),
    sms_url: normalizeString(chat?.sms_url),
    selected_by_default: Number(chat?.selected_by_default || 0),
    sort_order: Number(chat?.sort_order || 0),
  };
}

function normalizeDirectThreadMessage(message) {
  return {
    id: Number(message?.id || 0),
    chat_id: Number(message?.chat_id || 0),
    sender_type: normalizeString(message?.sender_type || "candidate").toLowerCase(),
    sender_name: normalizeString(message?.sender_name),
    sender_handle: normalizeString(message?.sender_handle),
    body_text: normalizeString(message?.body_text),
    message_kind: normalizeString(message?.message_kind || "text").toLowerCase(),
    attachment_name: normalizeString(message?.attachment_name),
    attachment_size: normalizeString(message?.attachment_size),
    attachment_url: normalizeString(message?.attachment_url),
    is_outbound: Boolean(message?.is_outbound),
    day_label: normalizeString(message?.day_label),
    time_label: normalizeString(message?.time_label),
    sent_at: message?.sent_at || null,
    sort_order: Number(message?.sort_order || 0),
  };
}

function buildComposeContextFromThread(thread, fallbackMessage = null) {
  if (!thread) return fallbackMessage;

  return {
    id: `thread-${thread.id}`,
    channel: "direct",
    candidate_name: thread.name || fallbackMessage?.candidate_name || "",
    candidate_title: thread.role || fallbackMessage?.candidate_title || "",
    candidate_email: thread.candidate_email || fallbackMessage?.candidate_email || "",
    candidate_phone: thread.candidate_phone || fallbackMessage?.candidate_phone || "",
    company_name: thread.company_name || fallbackMessage?.company_name || "",
    location: fallbackMessage?.location || "",
    stage: fallbackMessage?.stage || "",
    status: fallbackMessage?.status || "New",
    recruiter_name: thread.recruiter_name || fallbackMessage?.recruiter_name || "",
    recruiter_email: fallbackMessage?.recruiter_email || "",
    subject: fallbackMessage?.subject || "",
    preview_text: thread.preview || fallbackMessage?.preview_text || "",
    body_text: fallbackMessage?.body_text || thread.preview || "",
    from_name: fallbackMessage?.from_name || "",
    from_email: fallbackMessage?.from_email || "",
    to_email:
      thread.candidate_email || fallbackMessage?.to_email || fallbackMessage?.candidate_email || "",
    avatar_url: thread.avatar_url || fallbackMessage?.avatar_url || "",
    linkedin_url: thread.linkedin_url || fallbackMessage?.linkedin_url || "",
    email_url: thread.email_url || fallbackMessage?.email_url || "",
    sms_url: thread.sms_url || fallbackMessage?.sms_url || "",
    priority: fallbackMessage?.priority || "High",
    job_title: fallbackMessage?.job_title || "",
    contact_name: fallbackMessage?.contact_name || "",
    contact_email: fallbackMessage?.contact_email || "",
    hiring_manager: fallbackMessage?.hiring_manager || "",
    last_status: fallbackMessage?.last_status || "",
    top_match_label: fallbackMessage?.top_match_label || "",
    attachments: fallbackMessage?.attachments || [],
  };
}

function MessagesWorkspaceHeader({
  tab,
  onTabChange,
  tabs,
  counts,
  metaValue,
  dateText,
  newAction,
  onNewActionChange,
  searchValue,
  onSearchChange,
  onFilterClick,
}) {
  return (
    <Box>
      <CompanyProfileHeader
        title="Messages"
        tab={tab}
        onTabChange={onTabChange}
        tabs={tabs}
        counts={counts}
        metaLabel="Recruiter"
        metaValue={metaValue}
        dateText={dateText}
        showAction={true}
        newAction={newAction}
        onNewActionChange={onNewActionChange}
        actionPlaceholder="New"
        actionOptions={[{ value: "new_email", label: "New Email" }]}
        sx={{ borderBottom: `1px solid ${LIGHT_BLUE}` }}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr" },
          gridTemplateAreas: {
            xs: '"search" "filter"',
            md: '". search filter"',
          },
          alignItems: "center",
          gap: 1.25,
          px: 2,
          pt: 1.1,
          pb: 1.35,
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            gridArea: "search",
            display: "flex",
            justifyContent: "center",
            minWidth: 0,
          }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder="Search..."
            value={searchValue}
            onChange={onSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", md: 250 },
              maxWidth: { xs: "100%", md: 250 },
              "& .MuiOutlinedInput-root": {
                height: 34,
                borderRadius: 1.5,
                fontSize: 12.5,
                backgroundColor: "#fff",
              },
            }}
          />
        </Box>

        <Box
          sx={{
            gridArea: "filter",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            type="button"
            variant="contained"
            disableElevation
            startIcon={<Filter size="16" color="#fff" variant="Linear" />}
            onClick={onFilterClick}
            sx={{
              minWidth: "auto",
              height: 32,
              px: 1.5,
              borderRadius: 999,
              textTransform: "none",
              fontSize: 11.5,
              fontWeight: 700,
              lineHeight: 1,
              color: "#fff",
              backgroundColor: PURPLE,
              boxShadow: "none",
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: PURPLE,
                boxShadow: "none",
                opacity: 0.96,
              },
            }}
          >
            Filter By
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

function MessageExportDialog({ open, fields = [], selectedFieldIds = [], rowCount = 0, progress = 0, onClose, onToggleField, onExport }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle data-popup-header sx={{ pb: 0.5, fontSize: 18, fontWeight: 700 }}>Export messages</DialogTitle>
      <DialogContent sx={{ pt: "12px !important" }}>
        <Typography sx={{ color: MUTED, fontSize: 13, mb: 1.25 }}>
          Choose the fields to include in the CSV for {rowCount} selected message{rowCount === 1 ? "" : "s"}.
        </Typography>
        {progress > 0 ? <Box sx={{ mb: 1.25 }}><LinearProgress variant="determinate" value={progress} /><Typography sx={{ color: MUTED, fontSize: 12, mt: 0.5 }}>Creating CSV… {progress}%</Typography></Box> : null}
        <Stack spacing={0.25}>
          {fields.map((field) => (
            <FormControlLabel
              key={field.id}
              control={
                <Checkbox
                  size="small"
                  checked={selectedFieldIds.includes(field.id)}
                  onChange={() => onToggleField(field.id)}
                />
              }
              label={field.label}
              sx={{ "& .MuiFormControlLabel-label": { fontSize: 13.5, color: "#344054" } }}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions data-popup-footer sx={{ px: 3, pb: 2 }}>
        <Button disabled={progress > 0} onClick={onClose} sx={{ textTransform: "none", color: MUTED }}>Cancel</Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!selectedFieldIds.length || progress > 0}
          onClick={onExport}
          sx={{ textTransform: "none", backgroundColor: PURPLE, "&:hover": { backgroundColor: PURPLE } }}
        >
          {progress > 0 ? "Creating…" : "Export CSV"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function WorkspaceSection({
  title,
  rows,
  selectedIds,
  onSelectedIdsChange,
  onPreview,
  onCompose,
  onMarkRead,
  onDelete,
  onBulkMarkRead,
  onBulkDelete,
  onExport,
  bulkDeleteSaving,
}) {
  const [moreActions, setMoreActions] = useState({ anchorEl: null, row: null });
  const selectedRows = useMemo(() => {
    const idSet = new Set((selectedIds || []).map((id) => String(id)));
    return rows.filter((row) => idSet.has(String(row?.id)));
  }, [rows, selectedIds]);

  const bulkActionsNode = (
    <BulkActionsBar
      selectedCount={selectedRows.length}
      selectionLabel="message"
      actions={[
        {
          type: "button",
          label: "Reply",
          onClick: () => onCompose(selectedRows[0]),
          emptySelectionMessage: "Select a message to reply.",
        },
        { type: "divider" },
        {
          type: "button",
          label: "Forward",
          onClick: () => onCompose(selectedRows[0]),
          emptySelectionMessage: "Select a message to forward.",
        },
        { type: "divider" },
        {
          type: "button",
          label: "Mark as Read",
          onClick: () => onBulkMarkRead(selectedRows),
          confirm: false,
          emptySelectionMessage: "Select at least one message to mark as read.",
        },
        { type: "divider" },
        {
          type: "button",
          label: "Delete",
          onClick: () => onBulkDelete(selectedRows),
          disabled: bulkDeleteSaving,
          confirmMessage: `Delete ${selectedRows.length} message${selectedRows.length === 1 ? "" : "s"}? This cannot be undone.`,
          confirmLabel: "Delete",
          emptySelectionMessage: "Select at least one message to delete.",
        },
        { type: "divider" },
        {
          type: "button",
          label: "Export",
          onClick: () => onExport(selectedRows),
          confirm: false,
          emptySelectionMessage: "Select at least one message to export.",
        },
      ]}
      sx={{ px: 0, py: 0.5 }}
    />
  );

  const renderRow = (row) => (
    <>
      <TableCell>
        <Box display="flex" alignItems="center" gap={1.25}>
          <Avatar src={row.avatar_url || undefined} sx={{ width: 28, height: 28, fontSize: 12 }}>
            {row.candidate_name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: 12.5, color: "#111827", fontWeight: 700 }}>
              {row.candidate_name || "-"}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: MUTED }}>
              {row.candidate_title || "-"}
            </Typography>
          </Box>
        </Box>
      </TableCell>

      <TableCell>
        <Box>
          <Typography
            sx={{ fontSize: 12.5, color: "#344054", cursor: "pointer" }}
            onClick={() => onPreview(row)}
          >
            {row.subject || "-"}
          </Typography>
          {row.sequence_name || row.message_provider ? (
            <Typography sx={{ mt: 0.35, fontSize: 11, color: MUTED }}>
              {[
                row.sequence_name,
                row.message_provider
                  ? `${row.message_provider.charAt(0).toUpperCase()}${row.message_provider.slice(1)}`
                  : row.message_source?.startsWith("sequence")
                    ? "Sequence"
                    : "",
              ]
                .filter(Boolean)
                .join(" | ")}
            </Typography>
          ) : null}
        </Box>
      </TableCell>

      <TableCell>{formatSectionDate(row.sent_at || row.last_sync_at)}</TableCell>

      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
        <Stack direction="row" spacing={0.35} justifyContent="flex-end">
          <IconButton aria-label="Compose email" size="small" sx={messageActionIconSx} onClick={() => onCompose(row)}>
            <EmailEditIcon />
          </IconButton>
          <IconButton aria-label="Call contact" size="small" sx={messageActionIconSx} disabled={!row.candidate_phone && !row.phone} onClick={() => { const phone = row.candidate_phone || row.phone; if (phone) window.location.href = `tel:${phone}`; }}>
            <PhoneIcon />
          </IconButton>
          <IconButton aria-label="Delete message" size="small" sx={messageActionIconSx} onClick={() => onDelete([row])}>
            <TrashIcon />
          </IconButton>
          <IconButton aria-label="More message actions" size="small" sx={messageActionIconSx} onClick={(event) => setMoreActions({ anchorEl: event.currentTarget, row })}>
            <MoreActionsIcon />
          </IconButton>
        </Stack>
      </TableCell>
    </>
  );

  return (
    <Box sx={{ pb: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          pt: 1.6,
          pb: 0.6,
          borderTop: `1px solid ${LIGHT_BLUE}`,
          borderBottom: `1px solid ${LIGHT_BLUE}`,
          mb: "10px",
        }}
      >
        <Typography data-popup-section sx={{ color: BLUE, fontSize: 14, fontWeight: 800 }}>{title}</Typography>
        <Typography sx={{ color: MUTED, fontSize: 11.5 }}>{rows.length} Messages</Typography>
      </Box>

      <GenericDataTable
        columns={sectionColumns}
        rows={rows}
        renderRow={renderRow}
        defaultOrderBy="sent_at"
        defaultOrderDirection="desc"
        includeSelectionColumn={true}
        exportFields={TABS_EXPORT_FIELDS}
        selectedIds={selectedIds}
        onSelectedIdsChange={onSelectedIdsChange}
        initialRowsPerPage={10}
        showResultsHeader={false}
        bulkActionsTop={bulkActionsNode}
        bulkActionsBottom={bulkActionsNode}
        topBulkActionsWrapperSx={{
          px: 2,
          pt: 0,
          mb: "10px",
          borderTop: "none",
        }}
        tableContainerSx={{
          mx: 2,
          width: "calc(100% - 32px)",
          boxSizing: "border-box",
          border: `1px solid ${BORDER}`,
          borderRadius: "6px",
          overflow: "hidden",
          backgroundColor: "#fff",
          "& table": {
            borderTop: "none !important",
          },
          "& .MuiTableHead-root .MuiTableRow-root": {
            borderTop: "none !important",
          },
        }}
        bottomBulkActionsWrapperSx={{
          px: 2,
          pt: "10px",
          pb: 0,
          borderBottom: "none",
        }}
        paginationBarSx={{
          px: 2,
          mt: 2,
          mb: 0,
        }}
      />
      <Menu anchorEl={moreActions.anchorEl} open={Boolean(moreActions.anchorEl)} onClose={() => setMoreActions({ anchorEl: null, row: null })}>
        <MenuItem onClick={() => { onPreview(moreActions.row); setMoreActions({ anchorEl: null, row: null }); }}>View message</MenuItem>
        <MenuItem onClick={() => { onMarkRead(moreActions.row); setMoreActions({ anchorEl: null, row: null }); }}>Mark as read</MenuItem>
      </Menu>
    </Box>
  );
}

function deriveStats(messages, stats) {
  if (stats && typeof stats === "object") {
    const email = Number(stats.email || 0);
    return {
      total: email,
      email,
      drafted: Number(stats.drafted || 0),
      scheduled: Number(stats.scheduled || 0),
    };
  }

  return messages.reduce(
    (acc, message) => {
      if (message.channel === "emails") {
        acc.total += 1;
        acc.email += 1;
      }
      if (message.folder === "drafted") acc.drafted += 1;
      if (message.folder === "scheduled") acc.scheduled += 1;
      return acc;
    },
    { total: 0, email: 0, drafted: 0, scheduled: 0 }
  );
}

export default function MessagesSequences() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuthController();

  const [messages, setMessages] = useState([]);
  const [chats, setChats] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [pageLoading, setPageLoading] = useState(true);
  const [listChannel, setListChannel] = useState("emails");
  const [activeStatId, setActiveStatId] = useState("total");
  const [listSearch, setListSearch] = useState("");
  const [selectedListIds, setSelectedListIds] = useState([]);
  const [workspaceSelectedIds, setWorkspaceSelectedIds] = useState({
    unread: [],
    read: [],
    sent: [],
  });
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusToast, setStatusToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [exportDialog, setExportDialog] = useState({
    open: false,
    rows: [],
    fields: [],
    selectedFieldIds: [],
  });
  const [exportProgress, setExportProgress] = useState(0);
  const [viewMode, setViewMode] = useState("list");
  const [workspaceChannel, setWorkspaceChannel] = useState("emails");
  const [workspaceSearch, setWorkspaceSearch] = useState("");
  const [workspaceFolderFilter, setWorkspaceFolderFilter] = useState("all");
  const [workspaceFilterAnchorEl, setWorkspaceFilterAnchorEl] = useState(null);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [directSearch, setDirectSearch] = useState("");
  const [directComposer, setDirectComposer] = useState("");
  const [sendingDirect, setSendingDirect] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [previewMessage, setPreviewMessage] = useState(null);
  const [composeTarget, setComposeTarget] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useCsvImport();
  const [sendingInline, setSendingInline] = useState(false);
  const [newAction, setNewAction] = useState("");
  const [gmailStatus, setGmailStatus] = useState({
    configured: false,
    connected: false,
    connection: null,
  });
  const [gmailLoading, setGmailLoading] = useState(false);
  const sidebarResetToken = getSidebarResetToken(location, "/messages-sequences");

  useEffect(() => {
    if (!sidebarResetToken) return;

    setViewMode("list");
    setListChannel("emails");
    setActiveStatId("total");
    setWorkspaceChannel("emails");
    setWorkspaceSearch("");
    setWorkspaceFolderFilter("all");
    setWorkspaceSelectedIds({ unread: [], read: [], sent: [] });
    setWorkspaceFilterAnchorEl(null);
    setSelectedThreadId(null);
    setDirectSearch("");
    setDirectComposer("");
    setSelectedMessage(null);
    setPreviewMessage(null);
    setComposeTarget(null);
    setComposeOpen(false);
    setNewAction("");
  }, [sidebarResetToken]);

  const loadMessages = useCallback(async () => {
    setPageLoading(true);
    setLoadError("");

    try {
      const [response, sequenceEmailsResponse, websiteEmailsResponse] = await Promise.all([
        fetch(apiUrl("/messages?include_gmail=1"), {
          headers: authHeaders(),
        }),
        fetch(apiUrl("/gmail/sequence-messages"), {
          headers: authHeaders(),
        }),
        fetch(apiUrl("/gmail/website-messages"), {
          headers: authHeaders(),
        }),
      ]);

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(text || `Failed to load messages (${response.status})`);
      }

      const payload = await response.json();
      const sequenceEmailPayload = sequenceEmailsResponse.ok
        ? await sequenceEmailsResponse.json().catch(() => ({}))
        : {};
      const websiteEmailPayload = websiteEmailsResponse.ok
        ? await websiteEmailsResponse.json().catch(() => ({}))
        : {};
      if (!sequenceEmailsResponse.ok) {
        const gmailLoadError = await readResponseError(
          sequenceEmailsResponse,
          `Failed to load sequence Gmail messages (${sequenceEmailsResponse.status})`
        );
        console.error("Failed to load sequence Gmail messages:", gmailLoadError);
      }

      if (!websiteEmailsResponse.ok) {
        const gmailLoadError = await readResponseError(
          websiteEmailsResponse,
          `Failed to load website Gmail messages (${websiteEmailsResponse.status})`
        );
        console.error("Failed to load website Gmail messages:", gmailLoadError);
      }

      const baseMessages = (payload?.messages || []).map(normalizeMessage);
      const sequenceEmailMessages = (sequenceEmailPayload?.messages || []).map(normalizeMessage);
      const websiteEmailMessages = (websiteEmailPayload?.messages || []).map(normalizeMessage);
      const isWebsiteEmail = (message) => {
        const source = String(message?.message_source || "").toLowerCase();
        return (
          source === "website_gmail_reply" ||
          (source === "gmail" && Boolean(message?.message_metadata?.composed_via_gmail))
        );
      };
      // This view is email-only. Do not retain non-email data in page state,
      // which also prevents it from appearing through search or navigation.
      const mergedMessages = [
        ...sequenceEmailMessages,
        ...websiteEmailMessages,
        ...baseMessages.filter(
          (message) => message.channel === "emails" && (isWebsiteEmail(message) || message.message_source === "csv_import")
        ),
      ].filter((message, index, allMessages) => {
        const id = message.gmail_message_id || `message:${message.id}`;
        return allMessages.findIndex(
          (candidate) => (candidate.gmail_message_id || `message:${candidate.id}`) === id
        ) === index;
      }).sort((a, b) => {
        const bTime = toTimeValue(b.sent_at || b.last_sync_at);
        const aTime = toTimeValue(a.sent_at || a.last_sync_at);
        return bTime - aTime;
      });

      setMessages(mergedMessages);
      setChats((payload?.chats || []).map(normalizeChat));
      setThreadMessages((payload?.direct_thread_messages || []).map(normalizeDirectThreadMessage));
      setStats(null);
    } catch (error) {
      console.error("Failed to load messages page:", error);
      setLoadError(error?.message || "Failed to load messages.");
    } finally {
      setPageLoading(false);
    }
  }, []);

  const loadGmailStatus = useCallback(async () => {
    try {
      const response = await fetch(apiUrl("/gmail/status"), {
        headers: authHeaders(),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(text || `Failed to load Gmail status (${response.status})`);
      }

      const payload = await response.json().catch(() => ({}));
      setGmailStatus({
        configured: Boolean(payload?.configured),
        connected: Boolean(payload?.connected),
        connection:
          payload?.connection && typeof payload.connection === "object"
            ? payload.connection
            : null,
      });
    } catch (error) {
      console.error("Failed to load Gmail status:", error);
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    loadMessages();
  }, [loadMessages, loading]);

  useEffect(() => {
    loadGmailStatus();
  }, [loadGmailStatus]);

  const gmailHasSendScope = useMemo(
    () => hasGmailScope(gmailStatus.connection?.scope, GMAIL_SEND_SCOPE),
    [gmailStatus.connection?.scope]
  );

  const gmailHasComposeScope = useMemo(
    () => hasGmailScope(gmailStatus.connection?.scope, GMAIL_COMPOSE_SCOPE),
    [gmailStatus.connection?.scope]
  );

  const effectiveStats = useMemo(
    () => deriveStats(messages, stats),
    [messages, stats]
  );

  const topStats = useMemo(
    () => [
      { id: "total", value: formatStatValue(effectiveStats.total), label: "Total" },
      { id: "email", value: formatStatValue(effectiveStats.email), label: "Emails" },
      { id: "drafted", value: formatStatValue(effectiveStats.drafted), label: "Drafted" },
      { id: "scheduled", value: formatStatValue(effectiveStats.scheduled), label: "Scheduled" },
    ],
    [effectiveStats]
  );

  const channelCounts = useMemo(
    () => ({
      emails: Number(effectiveStats.email || 0),
    }),
    [effectiveStats]
  );

  const listRows = useMemo(() => {
    let filtered = messages.filter((message) => message.channel === listChannel);

    if (activeStatId === "drafted") {
      filtered = filtered.filter((message) => String(message?.folder || "").trim().toLowerCase() === "drafted");
    } else if (activeStatId === "scheduled") {
      filtered = filtered.filter((message) => String(message?.folder || "").trim().toLowerCase() === "scheduled");
    }

    const q = listSearch.trim().toLowerCase();
    if (!q) return filtered;

    return filtered.filter((message) =>
      [
        message.candidate_name,
        message.candidate_title,
        message.company_name,
        message.location,
        message.stage,
        message.recruiter_name,
        message.subject,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [messages, listChannel, listSearch, activeStatId]);

  const handleStatsItemClick = useCallback((item) => {
    const statId = String(item?.id || "");
    if (!statId || statId === "total") {
      setActiveStatId("total");
      return;
    }

    if (statId === "email") {
      setListChannel("emails");
      setSelectedListIds([]);
      setActiveStatId("email");
      return;
    }
    if (statId === "drafted" || statId === "scheduled") {
      setActiveStatId((prev) => (prev === statId ? "total" : statId));
    }
  }, []);

  const selectedListRows = useMemo(() => {
    const idSet = new Set((selectedListIds || []).map((id) => String(id)));
    return listRows.filter((row) => idSet.has(String(row?.id)));
  }, [listRows, selectedListIds]);

  const listSidebarMessage = useMemo(
    () => selectedListRows[0] || listRows[0] || null,
    [listRows, selectedListRows]
  );

  const messageStatusOptions = useMemo(() => {
    const options = Array.from(
      new Set(
        messages
          .map((message) => normalizeString(message?.status))
          .filter(Boolean)
      )
    );

    return Array.from(new Set([...(options.length
      ? options
      : ["New", "Contacted", "Interviewing", "Available", "Hired", "Not Available", "Sent", "Draft", "Synced"]), "Interested", "Not Interested"]));
  }, [messages]);

  const handleBulkStatusChange = useCallback(
    async (status) => {
      const nextStatus = normalizeString(status);
      if (!nextStatus || !selectedListRows.length || statusSaving) return;

      const ids = selectedListRows
        .map((row) => Number(row?.id))
        .filter((value) => Number.isFinite(value) && value > 0);

      if (!ids.length) return;

      setStatusSaving(true);
      setLoadError("");

      try {
        const response = await fetch(apiUrl("/messages/bulk-status"), {
          method: "PATCH",
          headers: authHeaders(true),
          body: JSON.stringify({ ids, status: nextStatus }),
        });

        if (!response.ok) {
          const message = await readResponseError(response, "Failed to update message status.");
          throw new Error(message);
        }

        const payload = await response.json().catch(() => ({}));
        const appliedStatus = normalizeString(payload?.status) || nextStatus;
        interviewsChanged();
        const idSet = new Set(ids.map((id) => String(id)));

        const applyStatusToMessage = (message) => {
          if (!message || !idSet.has(String(message?.id))) return message;
          return { ...message, status: appliedStatus, last_status: appliedStatus };
        };

        setMessages((prev) => prev.map((message) => applyStatusToMessage(message)));
        setSelectedMessage((prev) => applyStatusToMessage(prev));
        setPreviewMessage((prev) => applyStatusToMessage(prev));
        setStatusToast({
          open: true,
          severity: "success",
          message: `Updated ${ids.length} message${ids.length === 1 ? "" : "s"} to "${appliedStatus}".`,
        });
      } catch (error) {
        console.error("Failed to bulk update message statuses:", error);
        setLoadError(error?.message || "Failed to update message statuses.");
        setStatusToast({
          open: true,
          severity: "error",
          message: error?.message || "Failed to update message statuses.",
        });
      } finally {
        setStatusSaving(false);
      }
    },
    [selectedListRows, statusSaving]
  );

  const handleBulkDeleteMessages = useCallback(async (_event, options = {}) => {
    if (!selectedListRows.length || deleteSaving) return;

    if (listChannel === "emails") {
      window.alert("Sequence email deletion is not supported from this view.");
      return;
    }

    const ids = selectedListRows
      .map((row) => Number(row?.id))
      .filter((value) => Number.isFinite(value) && value > 0);

    if (!ids.length) return;

    if (!options?.confirmedByBulkActionsBar) {
      const confirmed = window.confirm(
        `Delete ${ids.length} message${ids.length === 1 ? "" : "s"}? This cannot be undone.`
      );
      if (!confirmed) return;
    }

    setDeleteSaving(true);
    setLoadError("");

    try {
      const response = await fetch(apiUrl("/messages/bulk-delete"), {
        method: "DELETE",
        headers: authHeaders(true),
        body: JSON.stringify({ ids }),
      });

      if (!response.ok) {
        const message = await readResponseError(response, "Failed to delete messages.");
        throw new Error(message);
      }

      const payload = await response.json().catch(() => ({}));
      const deletedIds = Array.isArray(payload?.deleted_ids)
        ? payload.deleted_ids
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0)
        : ids;
      const idSet = new Set(deletedIds.map((id) => String(id)));

      setMessages((prev) => prev.filter((message) => !idSet.has(String(message?.id))));
      setSelectedListIds([]);
      setSelectedMessage((prev) =>
        prev && idSet.has(String(prev?.id)) ? null : prev
      );
      setPreviewMessage((prev) =>
        prev && idSet.has(String(prev?.id)) ? null : prev
      );
    } catch (error) {
      console.error("Failed to bulk delete messages:", error);
      setLoadError(error?.message || "Failed to delete messages.");
    } finally {
      setDeleteSaving(false);
    }
  }, [deleteSaving, listChannel, selectedListRows]);

  const workspaceRows = useMemo(() => {
    const filtered = messages.filter((message) => message.channel === workspaceChannel);
    const q = workspaceSearch.trim().toLowerCase();
    if (!q || workspaceChannel === "direct") return filtered;

    return filtered.filter((message) =>
      [
        message.candidate_name,
        message.candidate_title,
        message.company_name,
        message.location,
        message.stage,
        message.recruiter_name,
        message.subject,
        message.preview_text,
        message.body_text,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [messages, workspaceChannel, workspaceSearch]);

  const visibleWorkspaceRows = useMemo(() => {
    if (workspaceChannel === "direct" || workspaceFolderFilter === "all") {
      return workspaceRows;
    }
    return workspaceRows.filter((row) => row.folder === workspaceFolderFilter);
  }, [workspaceChannel, workspaceFolderFilter, workspaceRows]);

  const groupedWorkspaceRows = useMemo(
    () => ({
      unread: workspaceRows.filter((row) => row.folder === "unread"),
      read: workspaceRows.filter((row) => row.folder === "read"),
      sent: workspaceRows.filter((row) => row.folder === "sent"),
    }),
    [workspaceRows]
  );

  const workspaceSections = useMemo(() => {
    const sections = [
      { id: "unread", title: "Unread", rows: groupedWorkspaceRows.unread },
      { id: "read", title: "Read", rows: groupedWorkspaceRows.read },
      { id: "sent", title: "Sent", rows: groupedWorkspaceRows.sent },
    ];

    if (workspaceFolderFilter === "all") return sections;
    return sections.filter((section) => section.id === workspaceFolderFilter);
  }, [groupedWorkspaceRows, workspaceFolderFilter]);

  const filteredThreads = useMemo(() => {
    const q = directSearch.trim().toLowerCase();
    if (!q) return chats;

    return chats.filter((thread) =>
      [
        thread.name,
        thread.handle,
        thread.role,
        thread.preview,
        thread.preview_attachment_name,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [chats, directSearch]);

  const selectedThread = useMemo(
    () =>
      chats.find((thread) => Number(thread.id) === Number(selectedThreadId)) ||
      null,
    [chats, selectedThreadId]
  );

  const selectedThreadMessages = useMemo(
    () =>
      threadMessages
      .filter((message) => Number(message.chat_id) === Number(selectedThread?.id || 0))
      .sort((a, b) => {
        const byOrder = Number(a.sort_order || 0) - Number(b.sort_order || 0);
        if (byOrder !== 0) return byOrder;
        return toTimeValue(a.sent_at) - toTimeValue(b.sent_at);
      }),
    [selectedThread?.id, threadMessages]
  );

  const selectedThreadEmails = useMemo(() => {
    if (!selectedThread) return [];

    const candidateMatches = messages.filter(
      (message) =>
        message.channel === "emails" &&
        ((selectedThread.candidate_email &&
          message.candidate_email === selectedThread.candidate_email) ||
          (!selectedThread.candidate_email &&
            message.candidate_name === selectedThread.name))
    );

    const recruiterMatches = candidateMatches.filter(
      (message) =>
        !selectedThread.recruiter_name ||
        !message.recruiter_name ||
        message.recruiter_name === selectedThread.recruiter_name
    );

    return (recruiterMatches.length ? recruiterMatches : candidateMatches)
      .sort(
        (a, b) =>
          toTimeValue(b.last_sync_at || b.sent_at) - toTimeValue(a.last_sync_at || a.sent_at)
      )
      .slice(0, 3);
  }, [messages, selectedThread]);

  const totalDirectUnread = useMemo(
    () => chats.reduce((sum, thread) => sum + Number(thread.unread_count || 0), 0),
    [chats]
  );

  const selectedThreadMessageContext = useMemo(() => {
    const fallbackEmail = selectedThreadEmails[0] || null;
    return buildComposeContextFromThread(selectedThread, fallbackEmail);
  }, [selectedThread, selectedThreadEmails]);

  useEffect(() => {
    if (viewMode !== "workspace") return;
    if (workspaceChannel === "direct") return;
    const stillVisible =
      selectedMessage &&
      visibleWorkspaceRows.some(
        (message) => Number(message.id) === Number(selectedMessage.id)
      );
    if (stillVisible) return;

    const nextMessage = visibleWorkspaceRows[0] || null;
    setSelectedMessage(nextMessage);
  }, [selectedMessage, viewMode, workspaceChannel, visibleWorkspaceRows]);

  useEffect(() => {
    if (viewMode !== "workspace" || workspaceChannel !== "direct") return;
    if (selectedThread && chats.some((thread) => Number(thread.id) === Number(selectedThread.id))) {
      return;
    }

    const preferredThread =
      chats.find((thread) => Number(thread.selected_by_default || 0) === 1) || chats[0] || null;

    setSelectedThreadId(preferredThread ? preferredThread.id : null);
  }, [chats, selectedThread, viewMode, workspaceChannel]);

  const handleWorkspaceTabChange = useCallback((nextChannel) => {
    if (nextChannel === workspaceChannel) return;
    if (nextChannel === "direct") setDirectSearch(workspaceSearch);
    else if (workspaceChannel === "direct") setWorkspaceSearch(directSearch);
    setWorkspaceChannel(nextChannel);
    setWorkspaceFolderFilter("all");
  }, [workspaceChannel, workspaceSearch, directSearch]);

  const handleOpenWorkspaceFilter = useCallback((event) => {
    setWorkspaceFilterAnchorEl(event.currentTarget);
  }, []);

  const handleCloseWorkspaceFilter = useCallback(() => {
    setWorkspaceFilterAnchorEl(null);
  }, []);

  const handleSelectWorkspaceFilter = useCallback((nextFilter) => {
    setWorkspaceFolderFilter(nextFilter);
    setWorkspaceFilterAnchorEl(null);
  }, []);

  const openWorkspace = (message) => {
    setSelectedMessage(message);
    setWorkspaceChannel(message.channel);
    setWorkspaceSearch("");
    setWorkspaceFolderFilter("all");
    if (message.channel === "direct") {
      const matchingThread =
        chats.find(
          (thread) =>
            (thread.candidate_email &&
              thread.candidate_email === message.candidate_email) ||
            thread.name === message.candidate_name
        ) || null;
      setSelectedThreadId(matchingThread?.id || null);
    }
    setViewMode("workspace");
  };

  const updateMessageState = useCallback((updatedMessage) => {
    const normalized = normalizeMessage(updatedMessage);
    setMessages((prev) => {
      const exists = prev.some((message) => Number(message.id) === Number(normalized.id));
      if (!exists) return [normalized, ...prev];
      return prev.map((message) =>
        Number(message.id) === Number(normalized.id) ? { ...message, ...normalized } : message
      );
    });
    setSelectedMessage((prev) =>
      prev && Number(prev.id) === Number(normalized.id) ? { ...prev, ...normalized } : prev
    );
    setPreviewMessage((prev) =>
      prev && Number(prev.id) === Number(normalized.id) ? { ...prev, ...normalized } : prev
    );
  }, []);

  const updateThreadState = useCallback((updatedThread) => {
    if (!updatedThread?.id) return;
    const normalized = normalizeChat(updatedThread);
    setChats((prev) =>
      prev.map((thread) =>
        Number(thread.id) === Number(normalized.id) ? { ...thread, ...normalized } : thread
      )
    );
  }, []);

  const handleSelectThread = useCallback((thread) => {
    setSelectedThreadId(thread?.id || null);
    setChats((prev) =>
      prev.map((item) =>
        Number(item.id) === Number(thread?.id)
          ? { ...item, unread_count: 0 }
          : item
      )
    );
  }, []);

  const handleMarkRead = useCallback(
    async (message) => {
      if (!message?.id) return;
      setLoadError("");

      try {
        const isGmailBacked =
          Boolean(message.gmail_message_id) &&
          String(message.message_source || "").includes("gmail");
        const response = await fetch(
          apiUrl(
            isGmailBacked
              ? `/gmail/messages/${encodeURIComponent(message.id)}/mark-read`
              : `/messages/${encodeURIComponent(message.id)}/mark-read`
          ),
          {
            method: "PATCH",
            headers: authHeaders(),
          }
        );

        if (!response.ok) {
          throw new Error(
            await readResponseError(
              response,
              `Failed to mark message as read (${response.status})`
            )
          );
        }

        const payload = await response.json().catch(() => ({}));
        updateMessageState(payload?.message || { ...message, folder: "read" });
      } catch (error) {
        console.error("Failed to mark message as read:", error);
        setLoadError(error?.message || "Failed to mark message as read.");
      }
    },
    [updateMessageState]
  );

  const handleBulkMarkRead = useCallback(
    async (selectedRows) => {
      const rows = Array.isArray(selectedRows) ? selectedRows.filter((row) => row?.id) : [];
      if (!rows.length) return;

      setLoadError("");
      const results = await Promise.allSettled(
        rows.map(async (message) => {
          const isGmailBacked =
            Boolean(message.gmail_message_id) &&
            String(message.message_source || "").includes("gmail");
          const response = await fetch(
            apiUrl(
              isGmailBacked
                ? `/gmail/messages/${encodeURIComponent(message.id)}/mark-read`
                : `/messages/${encodeURIComponent(message.id)}/mark-read`
            ),
            { method: "PATCH", headers: authHeaders() }
          );

          if (!response.ok) {
            throw new Error(await readResponseError(response, "Failed to mark message as read."));
          }

          const payload = await response.json().catch(() => ({}));
          updateMessageState(payload?.message || { ...message, folder: "read" });
          return message.id;
        })
      );

      const failed = results.find((result) => result.status === "rejected");
      if (failed) {
        const succeeded = results.filter((result) => result.status === "fulfilled").length;
        setLoadError(
          `${succeeded ? `${succeeded} message${succeeded === 1 ? " was" : "s were"} marked as read, but ` : ""}${failed.reason?.message || "some messages could not be updated."}`
        );
      }
    },
    [updateMessageState]
  );

  const handleBulkDeleteWorkspace = useCallback(
    async (selectedRows) => {
      const ids = (Array.isArray(selectedRows) ? selectedRows : [])
        .map((row) => Number(row?.id))
        .filter((id) => Number.isFinite(id) && id > 0);
      if (!ids.length || deleteSaving) return;

      setDeleteSaving(true);
      setLoadError("");
      try {
        const response = await fetch(apiUrl("/messages/bulk-delete"), {
          method: "DELETE",
          headers: authHeaders(true),
          body: JSON.stringify({ ids }),
        });
        if (!response.ok) {
          throw new Error(await readResponseError(response, "Failed to delete messages."));
        }

        const payload = await response.json().catch(() => ({}));
        const deletedIds = Array.isArray(payload?.deleted_ids) ? payload.deleted_ids : ids;
        const idSet = new Set(deletedIds.map((id) => String(id)));
        setMessages((prev) => prev.filter((message) => !idSet.has(String(message?.id))));
        setWorkspaceSelectedIds((prev) =>
          Object.fromEntries(
            Object.entries(prev).map(([section, selected]) => [
              section,
              selected.filter((id) => !idSet.has(String(id))),
            ])
          )
        );
        setSelectedMessage((prev) => (prev && idSet.has(String(prev.id)) ? null : prev));
        setPreviewMessage((prev) => (prev && idSet.has(String(prev.id)) ? null : prev));
      } catch (error) {
        console.error("Failed to bulk delete workspace messages:", error);
        setLoadError(error?.message || "Failed to delete messages.");
      } finally {
        setDeleteSaving(false);
      }
    },
    [deleteSaving]
  );

  const handleOpenExport = useCallback((rows, fields) => {
    const exportRows = Array.isArray(rows) ? rows.filter(Boolean) : [];
    if (!exportRows.length) return;
    const labels = new Set();
    const exportFields = [
      ...(Array.isArray(fields) ? fields : []),
      ...Array.from(new Set(exportRows.flatMap((row) => Object.keys(row || {}))))
        .filter((id) => !["id", "actions", "info"].includes(String(id).toLowerCase()))
        .map((id) => ({
          id,
          label: String(id)
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .replace(/[_-]+/g, " ")
            .replace(/\b\w/g, (letter) => letter.toUpperCase()),
        })),
    ].filter((field) => {
      const key = String(field.label || field.id).toLowerCase().replace(/[^a-z0-9]/g, "");
      if (labels.has(key)) return false;
      labels.add(key);
      return true;
    });
    setExportDialog({
      open: true,
      rows: exportRows,
      fields: exportFields,
      selectedFieldIds: exportFields.map((field) => field.id),
    });
  }, []);

  const handleExportMessages = useCallback(async () => {
    const selectedFields = exportDialog.fields.filter((field) =>
      exportDialog.selectedFieldIds.includes(field.id)
    );
    if (!selectedFields.length || !exportDialog.rows.length) return;

    const csvValue = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const getValue = (row, fieldId) => {
      if (fieldId === "sent_at" || fieldId === "last_sync_at") {
        return formatSectionDate(row[fieldId]);
      }
      if (fieldId === "message_provider") {
        const value = row.message_provider || row.message_source || "";
        return value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : "";
      }
      return row[fieldId] || "";
    };
    setExportProgress(1);
    await new Promise((resolve) => window.setTimeout(resolve, 0));
    const lines = [selectedFields.map((field) => csvValue(field.label)).join(",")];
    const chunkSize = 250;
    for (let start = 0; start < exportDialog.rows.length; start += chunkSize) {
      lines.push(
        ...exportDialog.rows
          .slice(start, start + chunkSize)
          .map((row) => selectedFields.map((field) => csvValue(getValue(row, field.id))).join(","))
      );
      setExportProgress(Math.min(99, Math.round(((start + chunkSize) / exportDialog.rows.length) * 100)));
      await new Promise((resolve) => window.setTimeout(resolve, 0));
    }
    const csv = lines.join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "messages-export.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setExportProgress(100);
    await new Promise((resolve) => window.setTimeout(resolve, 150));
    setExportProgress(0);
    setExportDialog({ open: false, rows: [], fields: [], selectedFieldIds: [] });
  }, [exportDialog]);

  const submitMessage = useCallback(
    async (form, action = "send", baseMessage = null) => {
      const context =
        baseMessage ||
        composeTarget ||
        selectedMessage ||
        visibleWorkspaceRows[0] ||
        listRows[0] ||
        null;
      const formAttachments = Array.isArray(form?.attachments) ? form.attachments : [];
      const attachmentMetadata = formAttachments.length
        ? serializeComposerAttachments(formAttachments)
        : context?.attachments || [];

      const payload = {
        action,
        channel: "email",
        candidate_name: context?.candidate_name || "",
        candidate_title: context?.candidate_title || "",
        candidate_email: form?.to || form?.recipient || context?.candidate_email || "",
        candidate_phone: context?.candidate_phone || "",
        company_name: context?.company_name || "",
        location: context?.location || "",
        stage: context?.stage || "",
        status: context?.status || "New",
        recruiter_name: context?.recruiter_name || user?.full_name || user?.name || "",
        recruiter_email: context?.recruiter_email || user?.email || "",
        subject: form?.subject || context?.subject || "",
        body_text: form?.body || form?.message || context?.body_text || context?.preview_text || "",
        to_email: form?.to || form?.recipient || context?.candidate_email || "",
        cc_email: form?.cc || "",
        bcc_email: form?.bcc || "",
        priority: form?.priority || context?.priority || "High",
        from_name: user?.full_name || user?.name || context?.from_name || "",
        from_email: user?.email || context?.from_email || "",
        job_title: context?.job_title || "",
        contact_name: context?.contact_name || "",
        contact_email: context?.contact_email || "",
        hiring_manager: context?.hiring_manager || "",
        last_status: context?.last_status || "",
        top_match_label: context?.top_match_label || "",
        attachments: attachmentMetadata,
        gmail_thread_id: context?.gmail_thread_id || "",
      };

      setLoadError("");

      const shouldUseGmailCompose = action === "send" || action === "draft";
      if (shouldUseGmailCompose && !gmailStatus.connected) {
        throw new Error("Gmail is not connected. Connect Gmail to save drafts or send email.");
      }

      if (action === "send" && !gmailHasSendScope) {
        throw new Error("Reconnect Gmail to grant send access before sending email.");
      }

      if (action === "draft" && !gmailHasComposeScope) {
        throw new Error("Reconnect Gmail to grant draft access before saving drafts.");
      }

      const response = shouldUseGmailCompose
        ? await fetch(apiUrl("/gmail/compose"), {
            method: "POST",
            headers: authHeaders(),
            body: buildGmailComposeFormData(payload, formAttachments),
          })
        : await fetch(apiUrl("/messages"), {
            method: "POST",
            headers: authHeaders(true),
            body: JSON.stringify(payload),
          });

      if (!response.ok) {
        throw new Error(
          await readResponseError(
            response,
            shouldUseGmailCompose
              ? `Failed to save draft or send email (${response.status})`
              : `Failed to save message (${response.status})`
          )
        );
      }

      const result = await response.json().catch(() => ({}));
      if (result?.message) {
        updateMessageState(result.message);
      }

      return result?.message || payload;
    },
    [
      composeTarget,
      listRows,
      selectedMessage,
      updateMessageState,
      gmailHasComposeScope,
      gmailHasSendScope,
      gmailStatus.connected,
      user?.email,
      user?.full_name,
      user?.name,
      visibleWorkspaceRows,
    ]
  );

  const handleQuickSend = useCallback(
    async (form, baseMessage = null) => {
      try {
        setSendingInline(true);
        await submitMessage(form, "send", baseMessage || selectedMessage || null);
      } catch (error) {
        setLoadError(error?.message || "Failed to send quick email.");
        console.error("Failed to send quick email:", error);
      } finally {
        setSendingInline(false);
      }
    },
    [selectedMessage, submitMessage]
  );

  const handleOpenCompose = useCallback((message = null) => {
    const fallbackTarget =
      workspaceChannel === "direct"
        ? selectedThreadMessageContext || selectedMessage || listRows[0] || messages[0] || null
        : selectedMessage || visibleWorkspaceRows[0] || listRows[0] || messages[0] || null;

    setComposeTarget(message || fallbackTarget);
    setComposeOpen(true);
  }, [
    listRows,
    messages,
    selectedMessage,
    selectedThreadMessageContext,
    visibleWorkspaceRows,
    workspaceChannel,
  ]);

  const handleSendDirectMessage = useCallback(async () => {
    if (!selectedThread?.id || !String(directComposer || "").trim()) return;

    try {
      setSendingDirect(true);
      const response = await fetch(
        apiUrl(`/messages/direct-threads/${encodeURIComponent(selectedThread.id)}/messages`),
        {
          method: "POST",
          headers: authHeaders(true),
          body: JSON.stringify({ message: directComposer.trim() }),
        }
      );

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(text || `Failed to save direct message (${response.status})`);
      }

      const payload = await response.json().catch(() => ({}));
      if (payload?.message) {
        const normalizedMessage = normalizeDirectThreadMessage(payload.message);
        setThreadMessages((prev) => [...prev, normalizedMessage]);
      }
      if (payload?.thread) {
        updateThreadState(payload.thread);
      }
      setDirectComposer("");
    } catch (error) {
      console.error("Failed to send direct message:", error);
    } finally {
      setSendingDirect(false);
    }
  }, [directComposer, selectedThread?.id, updateThreadState]);

  const handleDeleteDirectThread = useCallback(async () => {
    if (!selectedThread?.id) return;

    const confirmed = window.confirm(
      `Delete the conversation with ${selectedThread.name || "this contact"}? This cannot be undone.`
    );
    if (!confirmed) return;

    setLoadError("");

    try {
      const response = await fetch(
        apiUrl(`/messages/direct-threads/${encodeURIComponent(selectedThread.id)}`),
        {
          method: "DELETE",
          headers: authHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(
          await readResponseError(response, "Failed to delete direct thread.")
        );
      }

      const deletedId = Number(selectedThread.id);
      setChats((prev) => prev.filter((thread) => Number(thread.id) !== deletedId));
      setThreadMessages((prev) =>
        prev.filter((message) => Number(message.chat_id) !== deletedId)
      );
      setSelectedThreadId((prev) =>
        Number(prev) === deletedId ? null : prev
      );
    } catch (error) {
      console.error("Failed to delete direct thread:", error);
      setLoadError(error?.message || "Failed to delete direct thread.");
    }
  }, [selectedThread]);

  const workspaceTabs = useMemo(
    () =>
      CHANNELS.map((channel) => ({
        value: channel.value,
        label: channel.label,
        countKey: channel.value,
      })),
    []
  );
  const listChannelLabel = useMemo(
    () => findTabLabel(CHANNELS, listChannel),
    [listChannel]
  );
  const workspaceChannelLabel = useMemo(
    () => findTabLabel(CHANNELS, workspaceChannel),
    [workspaceChannel]
  );

  useEffect(() => {
    if (newAction === "new_email") {
      handleOpenCompose();
      setNewAction("");
    }
  }, [handleOpenCompose, newAction]);

  const handleConnectGmail = useCallback(async ({ usePopup = true } = {}) => {
    let popup = null;
    try {
      setGmailLoading(true);
      setLoadError("");

      if (usePopup) {
        popup = window.open(
          "",
          "gmail-oauth",
          "width=640,height=760,menubar=no,toolbar=no,status=no"
        );

        // The popup may already have navigated to Google by the time this
        // runs. Accessing popup.document outside this guarded helper throws a
        // cross-origin DOMException and incorrectly surfaces an app error.
        renderGmailPopupStatus(popup, "Connect Gmail", "Connecting to Gmail...");
      }

      const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const response = await fetch(
        apiUrl(
          `/gmail/auth-url?origin=${encodeURIComponent(
            window.location.origin
          )}&return_path=${encodeURIComponent(returnPath)}`
        ),
        {
          headers: authHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(
          await readResponseError(
            response,
            `Failed to start Gmail connection (${response.status})`
          )
        );
      }

      const payload = await response.json().catch(() => ({}));
      const authUrl = payload?.auth_url || "";
      if (!authUrl) {
        throw new Error("Failed to create Gmail authorization URL.");
      }

      if (!usePopup) {
        window.location.assign(authUrl);
        return;
      }

      if (!popup) {
        window.location.assign(authUrl);
        return;
      }

      popup.location.href = authUrl;
    } catch (error) {
      console.error("Failed to connect Gmail:", error);
      const nextMessage = error?.message || "Failed to connect Gmail.";
      renderGmailPopupStatus(popup, "Gmail Connection Error", nextMessage);
      setLoadError(nextMessage);
    } finally {
      setGmailLoading(false);
    }
  }, []);

  useEffect(() => {
    const oauthParams = getGmailOAuthParams();
    if (!oauthParams) return;

    clearGmailOAuthParams();

    if (oauthParams.status === "success") {
      loadGmailStatus();
      loadMessages();
      return;
    }

    setLoadError(oauthParams.error || "Failed to connect Gmail.");
  }, [loadGmailStatus, loadMessages]);

  useEffect(() => {
    const apiOrigin = (() => {
      try {
        return new URL(API_ROOT, window.location.origin).origin;
      } catch {
        return window.location.origin;
      }
    })();

    const handlePopupMessage = async (event) => {
      if (!event?.data || event.data.source !== "gmail-oauth") return;
      if (apiOrigin && event.origin !== apiOrigin) return;

      if (event.data.success) {
        await loadGmailStatus();
        await loadMessages();
        return;
      }

      setLoadError(event.data.error || "Failed to connect Gmail.");
    };

    window.addEventListener("message", handlePopupMessage);
    return () => window.removeEventListener("message", handlePopupMessage);
  }, [loadGmailStatus, loadMessages]);

  if (loading || pageLoading) {
    return (
      <Box sx={{ py: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={18} />
          <Typography sx={{ fontWeight: 700 }}>
            Loading messages...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (user?.role === "candidate") return null;

  const listBulkNode = (
      <BulkActionsBar
        selectedCount={selectedListRows.length}
        selectionLabel="message"
      actions={[
        { type: "button", label: "Move Stage" },
        { type: "divider" },
        {
          type: "menu",
          label: statusSaving ? "Saving..." : "Change Status",
          options: messageStatusOptions,
          onSelect: handleBulkStatusChange,
          feedback: statusToast,
          onFeedbackClose: () =>
            setStatusToast((prev) => ({ ...prev, open: false })),
          disabledMessage:
            !selectedListRows.length && !statusSaving
              ? "Please select an item to change status."
              : "",
          disabled: !selectedListRows.length || statusSaving,
        },
        { type: "divider" },
        {
          type: "button",
          label: deleteSaving ? "Deleting..." : "Delete",
          onClick: handleBulkDeleteMessages,
          disabled: !selectedListRows.length || deleteSaving,
        },
        { type: "divider" },
        {
          type: "button",
          label: "Export",
          onClick: () => handleOpenExport(selectedListRows, LIST_EXPORT_FIELDS),
          confirm: false,
          disabled: !selectedListRows.length,
        },
      ]}
      rightButton={{ label: "Match to Job", background: PURPLE }}
      sx={{ mb: 1.25 }}
    />
  );

  const renderListRow = (row) => (
    <>
      <TableCell>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar src={row.avatar_url || undefined} sx={{ width: 34, height: 34, fontSize: 14 }}>
            {row.candidate_name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ cursor: "pointer" }}
              onClick={() => openWorkspace(row)}
            >
              {row.candidate_name || "-"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.candidate_title || "-"}
            </Typography>
          </Box>
        </Box>
      </TableCell>

      <TableCell>
        <Chip label={row.status || "-"} size="small" sx={getStatusChipSx(row.status)} />
      </TableCell>

      <TableCell><CompanyTableCell row={row} /></TableCell>
      <TableCell>
        <TableLocationValue row={row} />
      </TableCell>
      <TableCell>{row.stage || "-"}</TableCell>

      <TableCell>
        <InfoActionIcons
          showGroupDivider={false}
          actions={[
            { type: "linkedin", href: row.linkedin_url },
            { type: "email", href: row.email_url || row.to_email },
            { type: "sms", href: row.sms_url },
          ]}
        />
      </TableCell>

      <TableCell>{formatRelativeTime(row.last_sync_at || row.sent_at)}</TableCell>
      <TableCell>{row.recruiter_name || "-"}</TableCell>
    </>
  );

  const listTabs = (
    <TabNavigationBar
      wrapperSx={{ px: 0, pt: 0.8 }}
      value={listChannel}
      onChange={(value) => {
        setListChannel(value);
        setSelectedListIds([]);
        if (value === "emails") setActiveStatId("email");
      }}
      items={CHANNELS.map((channel) => ({
        value: channel.value,
        label: channel.label,
        count: channelCounts[channel.value] || 0,
      }))}
      minHeight={36}
      borderBottom={false}
    />
  );

  const listContent = (
    <Card sx={{ p: 0, backgroundColor: "#fff", boxShadow: "none", borderRadius: 3, overflow: "hidden" }}>
      <CardContent sx={{ p: 0 }}>
        <TopListHeaderBar
          title="Messages"
          searchValue={listSearch}
          searchPlaceholder="Search..."
          onSearchChange={(event) => setListSearch(event.target.value)}
          actions={[
            {
              key: "filter",
              label: "Filter By",
              variant: "contained",
              startIcon: <Filter size="16" color="currentColor" />,
              onClick: () => console.log("Filter messages"),
            },
            {
              key: "import-csv",
              label: "Import CSV",
              variant: "contained",
              onClick: () => setCsvImportOpen(true),
            },
            {
              key: "gmail",
              label: gmailStatus.connected
                ? gmailHasSendScope && gmailHasComposeScope
                  ? gmailStatus.connection?.email_address
                    ? `Gmail Connected (${gmailStatus.connection.email_address})`
                    : "Gmail Connected"
                  : gmailStatus.connection?.email_address
                    ? `Reconnect Gmail (${gmailStatus.connection.email_address})`
                    : "Reconnect Gmail"
                : gmailLoading
                  ? "Connecting Gmail..."
                  : "Connect Gmail",
              variant: gmailStatus.connected ? "outlined" : "outlined",
              startIcon: <Sms size="16" color="currentColor" />,
              onClick: handleConnectGmail,
              disabled: gmailLoading,
            },
            {
              key: "compose",
              label: "New Email",
              variant: "contained",
              startIcon: <Sms size="16" color="#fff" />,
              onClick: () => handleOpenCompose(listRows[0] || messages[0] || null),
            },
          ]}
          resultsToolbarTargetId="messages-results-toolbar"
          tabContent={listTabs}
        />

        <GenericDataTable
          columns={listColumns}
          rows={listRows}
          renderRow={renderListRow}
          defaultOrderBy="last_sync_at"
          defaultOrderDirection="desc"
          includeSelectionColumn={true}
          exportFields={LIST_EXPORT_FIELDS}
          selectedIds={selectedListIds}
          onSelectedIdsChange={(ids) => setSelectedListIds(Array.isArray(ids) ? ids : [])}
          initialRowsPerPage={10}
          bulkActionsTop={listBulkNode}
          bulkActionsBottom={listBulkNode}
          resultsToolbarTargetId="messages-results-toolbar"
        />
      </CardContent>
    </Card>
  );

  const workspaceContent =
    workspaceChannel === "direct" ? (
      <DirectMessagesWorkspace
        threads={filteredThreads}
        selectedThread={selectedThread}
        searchValue={directSearch}
        onSearchChange={(event) => setDirectSearch(event.target.value)}
        onSelectThread={handleSelectThread}
        messages={selectedThreadMessages}
        composerValue={directComposer}
        onComposerChange={(event) => setDirectComposer(event.target.value)}
        onSend={handleSendDirectMessage}
        sending={sendingDirect}
        unreadCount={totalDirectUnread}
        onEmail={() => handleOpenCompose(selectedThreadMessageContext)}
        onCall={() => openContactCall(selectedThread || {})}
        onDelete={handleDeleteDirectThread}
        onMore={() => console.log("More direct thread actions")}
      />
    ) : (
      <Box sx={{ pt: 0.4 }}>
        {workspaceSections.map((section) => (
          <WorkspaceSection
            key={section.id}
            title={section.title}
            rows={section.rows}
            selectedIds={workspaceSelectedIds[section.id] || []}
            onSelectedIdsChange={(ids) =>
              setWorkspaceSelectedIds((prev) => ({
                ...prev,
                [section.id]: Array.isArray(ids) ? ids : [],
              }))
            }
            onPreview={setPreviewMessage}
            onCompose={handleOpenCompose}
            onMarkRead={handleMarkRead}
            onDelete={handleBulkDeleteWorkspace}
            onBulkMarkRead={handleBulkMarkRead}
            onBulkDelete={handleBulkDeleteWorkspace}
            onExport={(rows) => handleOpenExport(rows, TABS_EXPORT_FIELDS)}
            bulkDeleteSaving={deleteSaving}
          />
        ))}
      </Box>
    );

  const workspaceCompany =
    workspaceChannel === "direct"
      ? selectedThreadMessageContext || {}
      : selectedMessage || {};

  const workspaceTitle =
    workspaceChannel === "direct" ? "Direct Messages" : "Messages";

  const workspaceMetaValue =
    workspaceChannel === "direct"
      ? selectedThread?.recruiter_name || user?.full_name || user?.name || "Stan Smith"
      : selectedMessage?.recruiter_name || user?.full_name || user?.name || "Brian May";

  const workspaceDateText =
    workspaceChannel === "direct"
      ? selectedThread?.recruiter_date_label || "Tue 4 Feb 2025"
      : formatWorkspaceMetaDate(selectedMessage?.last_sync_at || selectedMessage?.sent_at);

  const workspaceHeaderSection =
    workspaceChannel === "direct" ? (
      <CompanyProfileHeader
        company={workspaceCompany}
        title={workspaceTitle}
        tab={workspaceChannel}
        onTabChange={handleWorkspaceTabChange}
        tabs={workspaceTabs}
        counts={channelCounts}
        metaLabel="Recruiter"
        metaValue={workspaceMetaValue}
        dateText={workspaceDateText}
        showAction={true}
        newAction={newAction}
        onNewActionChange={setNewAction}
        actionPlaceholder="New"
        actionOptions={[{ value: "new_email", label: "New Email" }]}
      />
    ) : (
      <MessagesWorkspaceHeader
        tab={workspaceChannel}
        onTabChange={handleWorkspaceTabChange}
        tabs={workspaceTabs}
        counts={channelCounts}
        metaValue={workspaceMetaValue}
        dateText={workspaceDateText}
        newAction={newAction}
        onNewActionChange={setNewAction}
        searchValue={workspaceSearch}
        onSearchChange={(event) => setWorkspaceSearch(event.target.value)}
        onFilterClick={handleOpenWorkspaceFilter}
      />
    );

  const workspaceSidebar =
    workspaceChannel === "direct" ? (
      <>
        <DirectMessagesQuickActionsCard
          onCall={() => openContactCall(selectedThread || {})}
          onEmail={() => handleOpenCompose(selectedThreadMessageContext)}
          onSchedule={() => openInterviewScheduler()}
          onCreateSequence={() => console.log("Create sequence")}
        />

        <Box sx={{ mt: 2 }}>
          <DirectMessagesAIAgentCard
            thread={selectedThread}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <DirectMessagesEmailsCard
            emails={selectedThreadEmails}
            onPreview={setPreviewMessage}
            onCompose={handleOpenCompose}
          />
        </Box>
      </>
    ) : (
      <>
        <MessagesQuickActionsCard
          onCall={() => openContactCall(selectedMessage || listSidebarMessage || {})}
          onEmail={() => handleOpenCompose(selectedMessage)}
          onSchedule={() => openInterviewScheduler()}
          onCreateSequence={() => console.log("Create sequence")}
        />

        <Box sx={{ mt: 2 }}>
          <MessagesAIAgentCard
            selectedMessage={selectedMessage}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <MessagesWriteEmailCard
            message={selectedMessage}
            onSend={(form) => handleQuickSend(form, selectedMessage)}
            sending={sendingInline}
          />
        </Box>

      </>
    );

  const listSidebar =
    listChannel === "emails" ? (
      <>
        <MessagesQuickActionsCard
          onCall={() => openContactCall(listSidebarMessage || {})}
          onEmail={() => handleOpenCompose(listSidebarMessage)}
          onSchedule={() => openInterviewScheduler()}
          onCreateSequence={() => console.log("Create sequence")}
        />

        <Box sx={{ mt: 2 }}>
          <MessagesAIAgentCard
            selectedMessage={listSidebarMessage}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <MessagesWriteEmailCard
            message={listSidebarMessage}
            onSend={(form) => handleQuickSend(form, listSidebarMessage)}
            sending={sendingInline}
          />
        </Box>

      </>
    ) : null;

  return (
    <Box sx={{ pb: 3 }}>
      {!!loadError && (
        <Box sx={{ pb: 2 }}>
          <Alert severity="error">{loadError}</Alert>
        </Box>
      )}

      {viewMode === "workspace" ? (
        <ListTableTabsTemplate
          rootSx={{ pb: 3 }}
          shellSx={{ pb: 0 }}
          breadcrumb={
            <Breadcrumb
              activeLabel={workspaceChannelLabel}
              items={[
                { label: "Overview", onClick: () => navigate("/overview") },
                { label: "Messages", path: "/messages-sequences" },
              ]}
            />
          }
          statsSection={
            <StatsSlider
              items={topStats}
              autoPlay={false}
              showArrows={true}
              cardMinWidth={200}
              gap={12}
              onItemClick={handleStatsItemClick}
              activeItemId={activeStatId}
            />
          }
          headerSection={workspaceHeaderSection}
          contentSection={workspaceContent}
          rightSidebar={workspaceSidebar}
          rightWidth={workspaceChannel === "direct" ? 300 : 360}
          leftColumnSx={{ border: `1px solid ${BORDER}`, borderRadius: 3 }}
        />
      ) : (
        <ListTableTabsTemplate
          breadcrumb={
            <Breadcrumb
              activeLabel={listChannelLabel}
              items={[
                { label: "Overview", onClick: () => navigate("/overview") },
                { label: "Messages", path: "/messages-sequences" },
              ]}
            />
          }
          statsSection={
            <StatsSlider
              items={topStats}
              autoPlay={false}
              showArrows={true}
              cardMinWidth={200}
              gap={12}
              onItemClick={handleStatsItemClick}
              activeItemId={activeStatId}
            />
          }
          contentSection={listContent}
          rightSidebar={listSidebar}
          rightWidth={listSidebar ? 360 : undefined}
        />
      )}

      <Menu
        anchorEl={workspaceFilterAnchorEl}
        open={Boolean(workspaceFilterAnchorEl)}
        onClose={handleCloseWorkspaceFilter}
        PaperProps={{
          sx: {
            mt: 0.5,
            borderRadius: 2,
            minWidth: 180,
            boxShadow: "0 12px 28px rgba(16, 24, 40, 0.14)",
          },
        }}
      >
        {WORKSPACE_FILTER_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={workspaceFolderFilter === option.value}
            onClick={() => handleSelectWorkspaceFilter(option.value)}
            sx={{ fontSize: 12.5 }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      <MessageExportDialog
        open={exportDialog.open}
        fields={exportDialog.fields}
        selectedFieldIds={exportDialog.selectedFieldIds}
        rowCount={exportDialog.rows.length}
        progress={exportProgress}
        onClose={() =>
          setExportDialog({ open: false, rows: [], fields: [], selectedFieldIds: [] })
        }
        onToggleField={(fieldId) =>
          setExportDialog((prev) => ({
            ...prev,
            selectedFieldIds: prev.selectedFieldIds.includes(fieldId)
              ? prev.selectedFieldIds.filter((id) => id !== fieldId)
              : [...prev.selectedFieldIds, fieldId],
          }))
        }
        onExport={handleExportMessages}
      />

      <MessagePreviewDialog
        open={Boolean(previewMessage)}
        onClose={() => setPreviewMessage(null)}
        message={previewMessage}
        onMarkRead={() => {
          if (previewMessage) handleMarkRead(previewMessage);
          setPreviewMessage(null);
        }}
        onForward={() => handleOpenCompose(previewMessage)}
        onReply={() => handleOpenCompose(previewMessage)}
      />

      <NewEmailCandidateDialog
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        message={composeTarget}
        onSaveDraft={async (form) => {
          try {
            setLoadError("");
            await submitMessage(form, "draft", composeTarget);
            setComposeOpen(false);
          } catch (error) {
            setLoadError(error?.message || "Failed to save draft.");
            console.error("Failed to save draft:", error);
            throw error;
          }
        }}
        onSendLater={async (form) => {
          try {
            setLoadError("");
            await submitMessage(form, "schedule", composeTarget);
            setComposeOpen(false);
          } catch (error) {
            setLoadError(error?.message || "Failed to schedule email.");
            console.error("Failed to schedule email:", error);
            throw error;
          }
        }}
        onSend={async (form) => {
          try {
            setLoadError("");
            await submitMessage(form, "send", composeTarget);
            setComposeOpen(false);
          } catch (error) {
            setLoadError(error?.message || "Failed to send email.");
            console.error("Failed to send email:", error);
            throw error;
          }
        }}
      />
      <CsvImportDialog open={csvImportOpen} onClose={() => setCsvImportOpen(false)} entityLabel="messages" importEndpoint={`${API_ROOT}/csv-import/messages`} onImported={() => window.location.reload()} />
    </Box>
  );
}
